import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useEffect, useMemo } from 'react';
import { useInteraction } from '@/common/hooks/useInteraction/useInteraction';
import { InteractionType } from '@/common/types/interaction';
import { useCore } from '$api-clients/hooks';
import type { OfferPackage, OfferPackageContact } from './types';

interface Props {
  id: string;
  type: InteractionType;
  open: boolean;
}

const PACKAGE_CONTACT_TYPES = ['agent', 'counterpartyAgent'];

export const useGetOfferPackage = ({ id, type, open }: Props) => {
  const isOffer = type === InteractionType.offer;
  const isLease = type === InteractionType.lease;
  const shouldFetchData = Boolean(id && open && (isOffer || isLease));

  // The interactions endpoint is type-agnostic and serves offers and leases alike.
  const {
    data: packages,
    isLoading: packagesLoading,
    error: packagesError,
  } = useCore(
    '/interactions/{interactionId}/packages',
    shouldFetchData
      ? { params: { path: { interactionId: id }, query: { limit: 1 } } }
      : null,
  );

  const {
    data: contactsData,
    isLoading: contactsLoading,
    error: contactsError,
  } = useCore(
    '/interactions/{interactionId}/contacts',
    shouldFetchData ? { params: { path: { interactionId: id } } } : null,
  );

  const {
    data: interaction,
    isLoading: interactionLoading,
    error: interactionError,
  } = useInteraction(shouldFetchData ? id : undefined, type);

  // Surface a single toast if any of the parallel requests fail.
  const error = packagesError ?? contactsError ?? interactionError;
  useEffect(() => {
    if (error !== undefined) {
      datadogRum.addError(error, {
        message: 'Error fetching offer package',
        payload: { interactionId: id },
      });
      toast(
        'error',
        'Something went wrong. Please try again or contact Side Support',
        { showCloseButton: true },
      );
    }
  }, [error, id]);

  const offerPackage: OfferPackage | null = packages?.[0] ?? null;

  const contacts = useMemo<OfferPackageContact[]>(
    () =>
      (contactsData ?? []).filter((contact) =>
        PACKAGE_CONTACT_TYPES.includes(contact.type),
      ),
    [contactsData],
  );

  const property = interaction?.property;

  const getPropertyName = () => {
    if (property?.street1 && property?.street2) {
      return `${property.street1}, ${property.street2}`;
    }
    if (property?.street1) {
      return property.street1;
    }
    return null;
  };

  return {
    offerPackage,
    propertyName: getPropertyName(),
    loading: packagesLoading || contactsLoading || interactionLoading,
    contacts,
  };
};
